/**
 * Platform Reviews — moderation queue for Super-Admin / Tenant Admin.
 * Route: /platform/reviews
 *
 * Backend role-scoping (see /api/marketplace/reviews/admin):
 *   - role=admin           → sees ALL tenants
 *   - role=owner | manager → sees only own tenant
 *
 * Status transitions allowed: pending → approved | rejected
 *                             approved → hidden | rejected
 *                             rejected | hidden → approved | pending
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Star, Check, XCircle, EyeOff, RefreshCw, Lock, ShieldAlert, FlaskConical, Clock, TrendingUp, Users, Reply, Trash2, Save, CheckCircle2, Activity, Mail } from "lucide-react";

const STATUS_TABS = [
  { id: "pending", labelAr: "بانتظار المراجعة", labelEn: "Pending", color: "amber" },
  { id: "approved", labelAr: "مُعتمدة", labelEn: "Approved", color: "emerald" },
  { id: "rejected", labelAr: "مرفوضة", labelEn: "Rejected", color: "rose" },
  { id: "hidden", labelAr: "مخفية", labelEn: "Hidden", color: "stone" },
];

const Stars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={12} className={i <= (n || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"} />
    ))}
    <span className="text-xs text-stone-600 ms-1 font-semibold">{n || 0}</span>
  </span>
);

const StatCard = ({ icon: Icon, label, value, tone = "stone", testId }) => (
  <div className={`bg-white rounded-xl border border-stone-200 p-3.5 flex items-center gap-3`} data-testid={testId}>
    <div className={`w-9 h-9 rounded-lg bg-${tone}-100 flex items-center justify-center shrink-0`}>
      <Icon size={16} className={`text-${tone}-600`} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className="text-xl font-bold text-stone-800 leading-tight">{value}</div>
    </div>
  </div>
);

// Phase 6 Sprint C — small "Notifications Foundation" dashboard. Reads the
// /api/platform/notifications/stats payload that already exists. Renders:
//   - 5 event counters (submitted / approved / rejected / hidden / replied)
//   - sparkline strip for the last 7 days per event
//   - avg moderation time (7d)
//   - reminder of provider stub + flag state
const EVENT_NAMES = [
  { id: "review.submitted",      labelAr: "مرسلة",   labelEn: "Submitted",  tone: "slate", icon: Mail },
  { id: "review.approved",       labelAr: "معتمدة",  labelEn: "Approved",   tone: "emerald", icon: Check },
  { id: "review.rejected",       labelAr: "مرفوضة",  labelEn: "Rejected",   tone: "rose",  icon: XCircle },
  { id: "review.hidden",         labelAr: "مخفية",   labelEn: "Hidden",     tone: "stone", icon: EyeOff },
  { id: "review.reply_created",  labelAr: "ردود",    labelEn: "Replies",    tone: "amber", icon: Reply },
];

function NotificationsDashboard({ isAR }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/platform/notifications/stats");
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    let t;
    refresh();
    t = setInterval(refresh, 30000);  // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  const t = (en, ar) => (isAR ? ar : en);

  if (loading || !data) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-4 animate-pulse" data-testid="notif-dash-loading">
        <div className="h-3 w-1/3 bg-stone-200 rounded mb-2" />
        <div className="h-2 w-3/4 bg-stone-100 rounded" />
      </div>
    );
  }

  const series = data.series_7d || {};
  const totalLast7 = Object.values(series).reduce((acc, arr) => acc + (arr || []).reduce((s, d) => s + d.n, 0), 0);

  // Compute global max for the sparkline so all rows share a y-scale
  const allMax = Math.max(
    1,
    ...Object.values(series).flatMap((arr) => (arr || []).map((d) => d.n)),
  );

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4" data-testid="notif-dash">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[#7A1F3D]" />
          <h3 className="font-bold text-stone-800 text-sm">
            {t("Reviews flow — last 7 days", "حركة المراجعات — آخر 7 أيام")}
          </h3>
          <span className="text-[10px] text-stone-500">
            ({t("provider", "المزود")}: <code className="text-stone-700">{data.provider}</code>)
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-stone-500">
          <span>{t("Total events", "إجمالي الأحداث")}: <strong className="text-stone-800">{data.events.total}</strong></span>
          <span className="text-stone-300">·</span>
          <span>{t("Pending", "بانتظار")}: <strong className="text-stone-800">{data.events.pending}</strong></span>
          <span className="text-stone-300">·</span>
          <span>
            {t("Avg moderation 7d", "متوسط الإشراف 7 أيام")}:{" "}
            <strong className="text-stone-800">
              {data.moderation_avg_minutes_7d != null
                ? `${data.moderation_avg_minutes_7d} ${t("min", "د")}`
                : "—"}
            </strong>
          </span>
        </div>
      </div>

      <div className="space-y-1.5">
        {EVENT_NAMES.map(({ id, labelAr, labelEn, tone, icon: Icon }) => {
          const row = series[id] || (data.days_7d || []).map((d) => ({ day: d, n: 0 }));
          const total = row.reduce((s, x) => s + x.n, 0);
          return (
            <div key={id} className="flex items-center gap-3" data-testid={`notif-row-${id}`}>
              <div className={`w-7 h-7 rounded-lg bg-${tone}-100 text-${tone}-600 flex items-center justify-center shrink-0`}>
                <Icon size={13} />
              </div>
              <div className="text-[11px] font-semibold text-stone-700 w-20 sm:w-24 shrink-0 truncate">
                {isAR ? labelAr : labelEn}
              </div>
              <div className="flex items-end gap-0.5 h-6 flex-1 min-w-0" dir="ltr">
                {row.map((d) => {
                  const h = (d.n / allMax) * 100;
                  return (
                    <div
                      key={d.day}
                      className={`flex-1 bg-${tone}-200 rounded-t hover:bg-${tone}-300 transition`}
                      style={{ height: `${Math.max(4, h)}%` }}
                      title={`${d.day} · ${d.n}`}
                    />
                  );
                })}
              </div>
              <div className="text-xs font-bold text-stone-800 w-8 text-end shrink-0">{total}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-stone-400 flex-wrap">
        <div>
          {(data.days_7d || []).map((d) => (
            <span key={d} className="me-2 font-mono">{d.slice(5)}</span>
          ))}
        </div>
        <div>
          {t("Sample of moderation", "عينة الإشراف")}: <strong>{data.moderation_sample_size_7d || 0}</strong>
          {" · "}
          {t("Total in window", "الإجمالي")}: <strong>{totalLast7}</strong>
        </div>
      </div>
    </div>
  );
}

export default function PlatformReviews() {
  const { user, loading: authLoading } = useAuth();
  const allowed = user && ["admin", "owner", "manager"].includes(user.role);
  const isAR = (document?.documentElement?.lang || "ar").startsWith("ar");
  const t = (en, ar) => (isAR ? ar : en);

  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [flagOff, setFlagOff] = useState(false);
  // Phase 6 — Beta monitoring stats
  const [stats, setStats] = useState(null);
  // Sprint A — per-row reply editor state
  const [replyOpen, setReplyOpen] = useState({});
  const [replyDraft, setReplyDraft] = useState({});
  const [replyBusy, setReplyBusy] = useState(null);

  const openReply = (rev) => {
    setReplyOpen({ ...replyOpen, [rev.review_id]: true });
    setReplyDraft({
      ...replyDraft,
      [rev.review_id]: {
        text_ar: rev.reply?.text_ar || "",
        text_en: rev.reply?.text_en || "",
      },
    });
  };
  const closeReply = (rid) => setReplyOpen({ ...replyOpen, [rid]: false });

  const saveReply = async (rev) => {
    const draft = replyDraft[rev.review_id] || {};
    if (!(draft.text_ar || "").trim() && !(draft.text_en || "").trim()) {
      toast.error(t("Reply text required", "نص الرد مطلوب"));
      return;
    }
    setReplyBusy(rev.review_id);
    try {
      await api.post(`/marketplace/reviews/${rev.review_id}/reply`, draft);
      toast.success(t("Reply saved", "تم حفظ الرد"));
      closeReply(rev.review_id);
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setReplyBusy(null);
    }
  };

  const deleteReply = async (rev) => {
    if (!confirm(t("Delete this reply?", "حذف هذا الرد؟"))) return;
    setReplyBusy(rev.review_id);
    try {
      await api.delete(`/marketplace/reviews/${rev.review_id}/reply`);
      toast.success(t("Reply deleted", "تم حذف الرد"));
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setReplyBusy(null);
    }
  };

  const loadStats = async () => {
    try {
      const r = await api.get("/marketplace/reviews/admin/stats");
      setStats(r.data);
    } catch (e) {
      // Non-fatal; stats are read-only and shouldn't block the queue
      setStats(null);
    }
  };

  const load = async (which = tab) => {
    setLoading(true);
    setFlagOff(false);
    try {
      const r = await api.get(`/marketplace/reviews/admin?status=${which}`);
      setItems(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 503) {
        setFlagOff(true);
        setItems([]);
      } else {
        toast.error(e?.response?.data?.detail || "Load failed");
      }
    } finally {
      setLoading(false);
    }
    // Always refresh stats alongside the queue
    loadStats();
  };

  useEffect(() => { if (allowed) load(tab); /* eslint-disable-next-line */ }, [tab, allowed]);

  const moderate = async (rid, status) => {
    setActing(rid);
    try {
      await api.put(`/marketplace/reviews/${rid}/moderate`, { status });
      toast.success(t("Updated", "تم التحديث"));
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setActing(null);
    }
  };

  if (authLoading) return <div className="p-8 text-center text-stone-400">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="mx-auto text-rose-500 mb-3" size={36} />
          <h2 className="text-xl font-bold text-stone-800">{t("Admin only", "للمشرفين فقط")}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="platform-reviews-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
            <ShieldAlert size={14} className="text-[#7A1F3D]" /> {t("Platform · Reviews moderation", "المنصة · إشراف التقييمات")}
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mt-1">{t("Reviews moderation", "إشراف التقييمات")}</h1>
          <p className="text-sm text-stone-500 mt-1">
            {user.role === "admin"
              ? t("Showing reviews across all tenants.", "عرض المراجعات لجميع الـ tenants.")
              : t("Showing reviews for your tenant only.", "عرض مراجعات الـ tenant الخاص بك فقط.")}
          </p>
        </div>
        <button onClick={() => load(tab)} className="px-3 py-1.5 text-sm border border-stone-200 rounded-lg hover:border-[#7A1F3D] inline-flex items-center gap-1.5" data-testid="reviews-refresh">
          <RefreshCw size={14} /> {t("Refresh", "تحديث")}
        </button>
      </div>

      {flagOff && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="reviews-flag-off">
          {t(
            "marketplace.reviews.enabled is OFF. Toggle it from Tenancy Admin to start moderating.",
            "العلَم marketplace.reviews.enabled مُطفأ. فعّله من Tenancy Admin لبدء الإشراف."
          )}
        </div>
      )}

      {/* Phase 6 — Closed-Beta banner + monitoring stats */}
      {stats && (
        <div className="bg-gradient-to-r from-violet-50 via-fuchsia-50 to-amber-50 border border-fuchsia-200/70 rounded-xl p-4" data-testid="reviews-beta-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-lg bg-fuchsia-600 text-white flex items-center justify-center shrink-0">
                <FlaskConical size={18} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-stone-800">
                    {stats.is_closed_beta
                      ? t("Reviews — Closed Beta", "التقييمات — تجربة مغلقة")
                      : stats.flag.state === "ga"
                        ? t("Reviews — GA", "التقييمات — متاحة للجميع")
                        : t("Reviews — Disabled", "التقييمات — معطّلة")}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${stats.flag.state === "beta" ? "bg-fuchsia-600 text-white" : stats.flag.state === "ga" ? "bg-emerald-600 text-white" : "bg-stone-400 text-white"}`}>
                    {(stats.flag.state || "off").toUpperCase()}
                  </span>
                </div>
                <div className="text-[11px] text-stone-600 mt-1">
                  {stats.flag.target_tenants?.length > 0 && (
                    <>{t("Active tenants:", "الـ tenants المُفعّلين:")}{" "}
                      {stats.flag.target_tenants.map((t) => (
                        <code key={t} className="px-1 py-0.5 rounded bg-white/80 text-fuchsia-700 mx-0.5">{t}</code>
                      ))}
                    </>
                  )}
                  {stats.flag.rollout_percentage > 0 && (
                    <> · {t("rollout", "نسبة الإطلاق")} {stats.flag.rollout_percentage}%</>
                  )}
                </div>
              </div>
            </div>
            <div className="text-[11px] text-stone-500 text-end">
              {t("Scope:", "النطاق:")} <strong className="text-stone-800">{stats.scope === "platform" ? t("All tenants", "كل الـ tenants") : t("Your tenant only", "tenant الحالي فقط")}</strong>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-4">
            <StatCard icon={Clock} label={t("Pending", "بانتظار")} value={stats.counts.pending} tone="amber" testId="stat-pending" />
            <StatCard icon={Check} label={t("Approved", "معتمدة")} value={stats.counts.approved} tone="emerald" testId="stat-approved" />
            <StatCard icon={XCircle} label={t("Rejected", "مرفوضة")} value={stats.counts.rejected} tone="rose" testId="stat-rejected" />
            <StatCard icon={EyeOff} label={t("Hidden", "مخفية")} value={stats.counts.hidden} tone="stone" testId="stat-hidden" />
            <StatCard
              icon={TrendingUp}
              label={t("Avg approval (30d)", "متوسط الموافقة (30 يوم)")}
              value={stats.avg_approval_minutes != null ? `${stats.avg_approval_minutes} ${t("min", "د")}` : "—"}
              tone="violet"
              testId="stat-avg-approval"
            />
          </div>

          {/* Top tenants (super-admin only) */}
          {stats.top_tenants?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
                <Users size={11} /> {t("Top tenants by reviews", "أعلى tenants من حيث التقييمات")}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="stat-top-tenants">
                {stats.top_tenants.map((tn) => (
                  <div key={tn.tenant_id} className="bg-white border border-stone-200 rounded-lg px-2.5 py-1 text-[11px] flex items-center gap-1.5">
                    <code className="text-stone-700">{tn.tenant_id}</code>
                    <span className="text-stone-400">·</span>
                    <span className="text-stone-700"><strong>{tn.total}</strong> {t("total", "إجمالي")}</span>
                    <span className="text-emerald-600">{tn.approved}↑</span>
                    <span className="text-amber-600">{tn.pending}…</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent moderation actions */}
          {stats.recent_actions?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5">
                {t("Recent moderation actions", "آخر إجراءات الإشراف")}
              </div>
              <div className="space-y-1" data-testid="stat-recent-actions">
                {stats.recent_actions.map((r) => (
                  <div key={r.review_id} className="text-[11px] bg-white border border-stone-200 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        r.status === "approved" ? "bg-emerald-100 text-emerald-700"
                        : r.status === "rejected" ? "bg-rose-100 text-rose-700"
                        : r.status === "hidden" ? "bg-stone-200 text-stone-700"
                        : "bg-amber-100 text-amber-700"
                      }`}>{r.status}</span>
                      <code className="text-stone-700">{r.review_id}</code>
                      <span className="text-stone-400">·</span>
                      <Stars n={r.ratings?.overall} />
                    </div>
                    <div className="text-stone-500">
                      {r.moderated_by} · {(r.moderated_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 6 Sprint C — small notifications/flow dashboard */}
      <NotificationsDashboard isAR={isAR} />

      <div className="flex gap-2 flex-wrap" data-testid="reviews-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s.id}
            onClick={() => setTab(s.id)}
            data-testid={`reviews-tab-${s.id}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
              tab === s.id ? "bg-[#1a1015] text-white border-[#1a1015]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}
          >
            {isAR ? s.labelAr : s.labelEn}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
        {loading && <div className="p-6 text-center text-stone-400 text-sm">{t("Loading...", "جاري التحميل...")}</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-stone-400 text-sm">{t("No reviews in this bucket.", "لا مراجعات في هذه الحالة.")}</div>
        )}
        {!loading && items.map((r) => (
          <div key={r.review_id} className="p-4 sm:p-5" data-testid={`review-row-${r.review_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Stars n={r.ratings?.overall} />
                  <code className="text-[10px] text-stone-500">{r.review_id}</code>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 font-mono">{r.tenant_id}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{r.status}</span>
                  {r.verified && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                      title={t("Verified booking", "حجز موثّق")}
                      data-testid={`verified-${r.review_id}`}
                    >
                      <CheckCircle2 size={10} /> {t("Verified", "موثّق")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  listing=<code>{r.listing_id}</code> · booking=<code>{r.booking_id}</code> · {(r.ts || "").slice(0, 16).replace("T", " ")}
                </div>
                {(r.text?.ar || r.text?.en) && (
                  <div className="mt-2 text-sm text-stone-700 leading-relaxed bg-stone-50 rounded-lg p-3" dir="auto">
                    {r.text?.ar || r.text?.en}
                  </div>
                )}
                <div className="mt-2 flex gap-3 text-[11px] text-stone-500 flex-wrap">
                  {r.ratings?.service != null && <span>{t("Service", "الخدمة")}: {r.ratings.service}</span>}
                  {r.ratings?.staff != null && <span>{t("Staff", "الموظف")}: {r.ratings.staff}</span>}
                  {r.ratings?.cleanliness != null && <span>{t("Cleanliness", "النظافة")}: {r.ratings.cleanliness}</span>}
                  {r.service_id && <span>service_id=<code>{r.service_id}</code></span>}
                  {r.staff_id && <span>staff_id=<code>{r.staff_id}</code></span>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                {r.status !== "approved" && (
                  <button onClick={() => moderate(r.review_id, "approved")} disabled={acting === r.review_id} data-testid={`approve-${r.review_id}`}
                    className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                    <Check size={12} /> {t("Approve", "اعتماد")}
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button onClick={() => moderate(r.review_id, "rejected")} disabled={acting === r.review_id} data-testid={`reject-${r.review_id}`}
                    className="px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                    <XCircle size={12} /> {t("Reject", "رفض")}
                  </button>
                )}
                {r.status === "approved" && (
                  <button onClick={() => moderate(r.review_id, "hidden")} disabled={acting === r.review_id} data-testid={`hide-${r.review_id}`}
                    className="px-3 py-1.5 rounded-lg bg-stone-500 hover:bg-stone-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50">
                    <EyeOff size={12} /> {t("Hide", "إخفاء")}
                  </button>
                )}
                <button onClick={() => openReply(r)} data-testid={`reply-toggle-${r.review_id}`}
                  className="px-3 py-1.5 rounded-lg border border-[#7A1F3D] text-[#7A1F3D] hover:bg-[#7A1F3D] hover:text-white text-xs inline-flex items-center gap-1">
                  <Reply size={12} /> {r.reply ? t("Edit reply", "تعديل الرد") : t("Reply", "رد")}
                </button>
              </div>
            </div>

            {/* Sprint A — existing reply preview */}
            {r.reply && !replyOpen[r.review_id] && (
              <div className="mt-3 ms-3 ps-3 border-s-2 border-[#7A1F3D]/40 bg-amber-50/40 rounded-r-lg py-2 pe-3" data-testid={`reply-preview-${r.review_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#8b6427]">
                    <Reply size={11} />
                    <span>{t("Salon's reply", "رد الصالون")}</span>
                    <span className="text-stone-400 font-normal">· {r.reply.replied_by} · {(r.reply.replied_at || "").slice(0, 16).replace("T", " ")}</span>
                    {r.reply.updated_at && (
                      <span className="text-[9px] text-stone-400 italic">({t("edited", "مُعدّل")})</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openReply(r)} className="text-[10px] text-stone-600 hover:text-[#7A1F3D] underline" data-testid={`reply-edit-${r.review_id}`}>{t("edit", "تعديل")}</button>
                    <button onClick={() => deleteReply(r)} disabled={replyBusy === r.review_id} className="text-[10px] text-rose-500 hover:text-rose-700 underline disabled:opacity-50" data-testid={`reply-delete-${r.review_id}`}>{t("delete", "حذف")}</button>
                  </div>
                </div>
                <p className="text-[13px] text-stone-700 mt-1.5 leading-relaxed whitespace-pre-wrap" dir="auto">
                  {r.reply.text_ar || r.reply.text_en}
                </p>
                {r.reply.text_ar && r.reply.text_en && (
                  <p className="text-[12px] text-stone-500 mt-1 leading-relaxed whitespace-pre-wrap" dir="ltr">{r.reply.text_en}</p>
                )}
              </div>
            )}

            {/* Sprint A — inline reply editor */}
            {replyOpen[r.review_id] && (
              <div className="mt-3 bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid={`reply-editor-${r.review_id}`}>
                <div className="text-[11px] font-semibold text-stone-700 mb-2 flex items-center gap-1.5">
                  <Reply size={11} className="text-[#7A1F3D]" />
                  {r.reply ? t("Edit reply", "تعديل الرد") : t("New reply", "رد جديد")}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <textarea
                    data-testid={`reply-ar-${r.review_id}`}
                    value={replyDraft[r.review_id]?.text_ar || ""}
                    onChange={(e) => setReplyDraft({ ...replyDraft, [r.review_id]: { ...replyDraft[r.review_id], text_ar: e.target.value.slice(0, 1000) } })}
                    placeholder={t("Arabic reply", "الرد بالعربية")}
                    rows={3}
                    dir="rtl"
                    className="w-full px-2.5 py-1.5 rounded border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
                  />
                  <textarea
                    data-testid={`reply-en-${r.review_id}`}
                    value={replyDraft[r.review_id]?.text_en || ""}
                    onChange={(e) => setReplyDraft({ ...replyDraft, [r.review_id]: { ...replyDraft[r.review_id], text_en: e.target.value.slice(0, 1000) } })}
                    placeholder={t("English reply", "الرد بالإنجليزية")}
                    rows={3}
                    dir="ltr"
                    className="w-full px-2.5 py-1.5 rounded border border-stone-200 focus:border-[#7A1F3D] outline-none text-sm"
                  />
                </div>
                <div className="text-[10px] text-stone-500 mt-1">
                  {t("Plain text only. Reply is visible publicly only when the review is approved.", "نص فقط. الرد يظهر علناً عندما تكون المراجعة معتمدة.")}
                </div>
                <div className="flex gap-2 justify-end mt-2">
                  <button onClick={() => closeReply(r.review_id)} className="px-2.5 py-1 rounded border border-stone-200 text-xs" data-testid={`reply-cancel-${r.review_id}`}>{t("Cancel", "إلغاء")}</button>
                  <button onClick={() => saveReply(r)} disabled={replyBusy === r.review_id} className="px-3 py-1 rounded bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`reply-save-${r.review_id}`}>
                    <Save size={11} /> {t("Save", "حفظ")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-[11px] text-stone-400">
        {t(
          "Public marketplace shows only `approved` reviews. Hidden/rejected items remain stored for audit.",
          "العامة ترى المراجعات `approved` فقط. المخفية والمرفوضة تبقى محفوظة للتدقيق."
        )}
      </div>
    </div>
  );
}
